import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { formatServicePeriod, getServiceDates, getServiceDays, getServiceTotal } from './billDetailsUtils';
import { DEFAULT_CAD_PERCENT, DEFAULT_GWD_PERCENT, getAllocationSetting, getAllocationSettingKey, round2, toNumber, toPercent } from './billEditUtils';

/**
 * Allocation suggestions have no endpoint yet: unit rosters, occupancy factors and recovery settings
 * belong to the allocations domain, which a freshly uploaded bill is not attached to. Until that data
 * is available the tables run on this fixed roster, so the layout can be reviewed end to end.
 */
const DEMO_UNITS = [{
  occupancyFactor: 1,
  occupants: 1,
  resident: 'Ava Martinez',
  unit: '1A'
}, {
  occupancyFactor: 1.2,
  occupants: 2,
  resident: 'Noah Johnson',
  unit: '1B'
}, {
  occupancyFactor: 1,
  occupants: 1,
  resident: 'Mia Chen',
  unit: '1C'
}, {
  occupancyFactor: 1.3,
  occupants: 3,
  resident: 'Ethan Williams',
  unit: '2A'
}, {
  occupancyFactor: 1.2,
  occupants: 2,
  resident: 'Sofia Garcia',
  unit: '2B'
}, {
  occupancyFactor: 1,
  occupants: 1,
  resident: 'Liam Pate',
  unit: '3B'
}, {
  occupancyFactor: 1.2,
  occupants: 2,
  resident: 'Olivia Brown',
  unit: '4A'
}, {
  occupancyFactor: 1,
  occupants: 1,
  resident: 'Jackson Lee',
  unit: '4B'
}];
const DEFAULT_DAYS = 31;
const FACTOR_TOTAL = DEMO_UNITS.reduce((acc, unit) => acc + unit.occupancyFactor, 0);
function splitByOccupancy(totalBilling, days) {
  if (totalBilling === null) return DEMO_UNITS.map(unit => _objectSpread(_objectSpread({}, unit), {}, {
    days,
    total: null
  }));
  let allocated = 0;
  return DEMO_UNITS.map((unit, index) => {
    const isLast = index === DEMO_UNITS.length - 1;
    // The last unit absorbs the rounding drift so the column still adds up to the billed total.
    const total = isLast ? round2(totalBilling - allocated) : round2(totalBilling * unit.occupancyFactor / FACTOR_TOTAL);
    allocated += total;
    return _objectSpread(_objectSpread({}, unit), {}, {
      days,
      total
    });
  });
}

/**
 * Build one allocation table per property/utility from the extracted bill. `settings` carries the
 * user's edits (service dates, bill amount, deductions) and `approvedKeys` the tables approved in
 * this session; everything else is derived on the spot, so a changed input immediately moves the
 * total billing, the recovery and the per-unit charges.
 */
export function buildDemoAllocations(data) {
  let settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let approvedKeys = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  return ((data === null || data === void 0 ? void 0 : data.properties) || []).map((property, propertyIndex) => ({
    address: property.address || '',
    propertyIndex,
    utilities: (property.services || []).map(service => {
      var _setting$serviceStart, _setting$serviceEndDa, _setting$cadPercent, _setting$gwdPercent, _getServiceDays;
      const setting = getAllocationSetting(settings, propertyIndex, service.serviceType);
      const key = getAllocationSettingKey(propertyIndex, service.serviceType);
      const extracted = getServiceDates(service, data);
      const extractedAmount = getServiceTotal(service);
      const start = (_setting$serviceStart = setting.serviceStartDate) !== null && _setting$serviceStart !== void 0 ? _setting$serviceStart : extracted.start;
      const end = (_setting$serviceEndDa = setting.serviceEndDate) !== null && _setting$serviceEndDa !== void 0 ? _setting$serviceEndDa : extracted.end;
      // An emptied amount field falls back to the extraction rather than billing nothing.
      const overriddenAmount = toNumber(setting.billAmount);
      const billAmount = overriddenAmount === null ? extractedAmount : overriddenAmount;
      const cadPercent = toPercent((_setting$cadPercent = setting.cadPercent) !== null && _setting$cadPercent !== void 0 ? _setting$cadPercent : DEFAULT_CAD_PERCENT);
      const gwdPercent = toPercent((_setting$gwdPercent = setting.gwdPercent) !== null && _setting$gwdPercent !== void 0 ? _setting$gwdPercent : DEFAULT_GWD_PERCENT);
      const deductionPercent = round2(Math.min(100, cadPercent + gwdPercent));
      const recoveryPercent = round2(100 - deductionPercent);
      const days = (_getServiceDays = getServiceDays(start, end)) !== null && _getServiceDays !== void 0 ? _getServiceDays : DEFAULT_DAYS;
      const totalBilling = billAmount === null ? null : round2(billAmount * (recoveryPercent / 100));
      return {
        billAmount,
        cadPercent,
        days,
        deductionPercent,
        extractedAmount,
        extractedPeriod: formatServicePeriod(extracted.start, extracted.end),
        gwdPercent,
        isApproved: approvedKeys.includes(key),
        key,
        propertyIndex,
        recoveryPercent,
        serviceEndDate: end,
        serviceStartDate: start,
        serviceType: service.serviceType,
        servicePeriod: formatServicePeriod(start, end),
        totalBilling,
        units: splitByOccupancy(totalBilling, days)
      };
    })
  }));
}